import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const CompoundInterest = () => {
   // State variables for Compound Interest calculation
   const [principal, setPrincipal] = useState(0); // Initial investment
   const [rate, setRate] = useState(0); // Annual interest rate (in %)
   const [timesCompounded, setTimesCompounded] = useState(1); // Times interest is compounded per year
   const [time, setTime] = useState(0); // Time the money is invested for (in years)
   const [amount, setAmount] = useState(0); // Calculated compound interest

   // Event handlers to update state values
   const handlePrincipalChange = (e) => setPrincipal(parseFloat(e.target.value) || 0);
   const handleRateChange = (e) => setRate(parseFloat(e.target.value) || 0);
   const handleTimesCompoundedChange = (e) => setTimesCompounded(parseInt(e.target.value) || 1);
   const handleTimeChange = (e) => setTime(parseInt(e.target.value) || 0);

   // useEffect hook to calculate compound interest when any input changes
   useEffect(() => {
     if (principal && rate && timesCompounded && time) {
       const rateDecimal = rate / 100; // Convert rate to decimal
       const compoundInterest = principal * Math.pow(1 + rateDecimal / timesCompounded, timesCompounded * time);
       setAmount(compoundInterest);
     } else {
       setAmount(0);
     }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

   }, [principal, rate, timesCompounded, time]);

  return (
    <Layout title="Compound Interest Calculator" path="compound-interest">
      <section className="section-padding" id="compound-interest-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Compound Interest Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Compound Interest Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Principal Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="principal" className="form-label">
                        Principal (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Principal:</strong> The initial amount of money invested or loaned."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="principal"
                        placeholder="Enter initial investment"
                        value={principal || ""}
                        onChange={handlePrincipalChange}
                      />
                    </div>

                    {/* Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="rate" className="form-label">
                        Annual Interest Rate (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Interest Rate:</strong> The annual rate of return on the investment."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="rate"
                        placeholder="Enter interest rate"
                        value={rate || ""}
                        onChange={handleRateChange}
                      />
                    </div>

                    {/* Times Compounded Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="timesCompounded" className="form-label">
                        Times Compounded per Year
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Compounding Frequency:</strong> The number of times interest is compounded per year."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="timesCompounded"
                        placeholder="Enter times compounded per year"
                        value={timesCompounded || ""}
                        onChange={handleTimesCompoundedChange}
                      />
                    </div>

                    {/* Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="time" className="form-label">
                        Time (years)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Investment Duration:</strong> The length of time the money is invested for, in years."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="time"
                        placeholder="Enter investment duration"
                        value={time || ""}
                        onChange={handleTimeChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Final Amount:{" "}
                        <span className="fw-bold ms-3">
                          {amount === 0 ? "0.00" : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Compound Interest Calculator
                </h3>

                {/* What is a Compound Interest Calculator? */}
                <h4 className="mt-3 mb-3">What is a Compound Interest Calculator?</h4>
                <p>
                  A **Compound Interest Calculator** helps you estimate the future value of an investment based on compound interest. It takes into account the principal amount, the interest rate, the frequency of compounding, and the time period the money is invested for.
                </p>

                {/* Why Use a Compound Interest Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Compound Interest Calculator?</h4>
                <p>
                  This calculator is useful for individuals who want to understand how their investments will grow over time. It helps you plan for financial growth by showing the impact of compound interest on your savings or investment, especially over longer periods of time.
                </p>

                {/* Benefits of Using a Compound Interest Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Compound Interest Calculator</h4>
                <ul>
                  <li><strong>Useful for Financial Growth Planning:</strong> Helps you forecast how your investment will grow over time.</li>
                  <li><strong>Provides Insight into Savings and Investment Growth:</strong> Shows how compound interest works and how different variables affect growth.</li>
                  <li><strong>Encourages Disciplined Saving Habits:</strong> Promotes saving regularly and investing wisely to benefit from compound interest.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Compound Interest Calculator?</h4>
                <p>
                  The Compound Interest Calculator is suitable for anyone interested in understanding how their money can grow over time, including:
                </p>
                <ul>
                  <li><strong>Personal Finance Enthusiasts:</strong> Helps you plan for long-term financial goals such as retirement.</li>
                  <li><strong>Banking and Investment Firms:</strong> Used by financial institutions to demonstrate investment growth to clients.</li>
                  <li><strong>Individuals Planning for Future Expenses:</strong> Useful for planning savings for major life events like buying a house or funding education.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How is compound interest different from simple interest?</h4>
                <p>
                  Compound interest is calculated on both the initial principal and the accumulated interest from previous periods. Simple interest is calculated only on the initial principal, meaning compound interest results in higher returns over time.
                </p>

                <h4 className="mt-4 mb-3">Why should I care about the compounding frequency?</h4>
                <p>
                  The frequency of compounding affects how often interest is calculated and added to the principal. More frequent compounding periods (e.g., monthly) result in higher overall returns compared to less frequent compounding (e.g., annually).
                </p>

                <h4 className="mt-4 mb-3">How does time impact compound interest?</h4>
                <p>
                  The longer the time period, the more time your investment has to grow and accumulate compound interest. Compound interest grows exponentially, meaning even small investments can grow significantly over long periods of time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CompoundInterest;
